import React, { useState, useEffect } from 'react';
import {
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Paper,
  Box,
  IconButton,
  FormControlLabel,
  Checkbox,
  Button,
  Collapse,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { 
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  UnfoldMore as UnfoldMoreIcon,
  UnfoldLess as UnfoldLessIcon
} from '@mui/icons-material';
import VariableSelector from '../VariableSelector';
import PropTypes from 'prop-types';
import { createDefaultMessage } from './reminderCampaignDefaults';
import { normalizeFromEndpoint } from '../../../utils/endpointUtils';
import { EmailConfig, SMSConfig, VoiceConfig, ChatConfig } from '../channels';
import MessageTemplateService from '../../../services/messageTemplateService';

const ReminderMessageConfig = ({
  message,
  onMessageChange,
  onDelete,
  templates,
  communicationEndpoints,
  channel,
  index,
  expandedVoiceConfigs,
  onToggleVoiceConfig,
  onExpandAllVoiceConfigs,
  onCollapseAllVoiceConfigs,
}) => {
  const [showVariables, setShowVariables] = useState(false);
  const [localMessage, setLocalMessage] = useState(message);
  const [variableCategories, setVariableCategories] = useState([]);

  useEffect(() => {
    setLocalMessage(message);
  }, [message]);

  // Load available variables
  useEffect(() => {
    const fetchAvailableVariables = async () => {
      try {
        const categories = await MessageTemplateService.getAvailableVariables();
        setVariableCategories(categories);
      } catch (err) {
        console.error('Failed to fetch available variables:', err);
      }
    };
    fetchAvailableVariables();
  }, []);

  const handleMessageChange = (updatedMessage) => {
    setLocalMessage(updatedMessage);
    onMessageChange(updatedMessage);
  };

  const handleConfigChange = (updatedConfig) => {
    onMessageChange({
      ...message,
      [`${channel}_config`]: updatedConfig,
    });
  };

  const handleEndpointChange = (value) => {
    onMessageChange({
      ...message,
      [`${channel}_config`]: {
        ...message[`${channel}_config`],
        from_endpoint: value,
      },
    });
  };

  const handleTemplateChange = (e) => {
    onMessageChange({
      ...message,
      template: e.target.value,
    });
  };

  const handleInsertVariable = (categoryName, variableName) => {
    const variable = `{{${categoryName}.${variableName}}}`;
    const currentContent = message.content || '';
    const newContent = currentContent + variable;
    
    handleMessageChange({
      ...message,
      content: newContent
    });
  };

  const renderChannelConfig = () => {
    const commonProps = {
      config: message[`${channel}_config`],
      onConfigChange: handleConfigChange,
      communicationEndpoints,
      onEndpointChange: handleEndpointChange,
    };

    switch (channel) {
      case 'email':
        return <EmailConfig {...commonProps} />;
      case 'sms':
        return <SMSConfig {...commonProps} />;
      case 'voice':
        return (
          <Box>
            {/* Collapsible Voice Configuration */}
            <Accordion
              expanded={expandedVoiceConfigs?.[index] || false}
              onChange={() => onToggleVoiceConfig(index)}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`voice-config-${index}-content`}
                id={`voice-config-${index}-header`}
              >
                <Typography variant="subtitle2">
                  Voice Platform Configuration
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <VoiceConfig {...commonProps} />
              </AccordionDetails>
            </Accordion>
          </Box>
        );
      case 'chat':
        return <ChatConfig {...commonProps} />;
      default:
        return null;
    }
  };

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Message {index + 1}</Typography>
        {onDelete && (
          <IconButton
            size="small"
            onClick={onDelete}
            color="error"
          >
            <DeleteIcon />
          </IconButton>
        )}
      </Box>

      <Grid container spacing={2}>
        {channel !== 'voice' && (
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Template</InputLabel>
              <Select
                value={message.template || ''}
                onChange={handleTemplateChange}
                label="Template"
              >
                <MenuItem value="">None</MenuItem>
                {templates.map((template) => (
                  <MenuItem key={template.id} value={template.id}>
                    {template.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        {!message.template && channel !== 'voice' && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Message Content"
              value={message.content || ''}
              onChange={(e) => {
                handleMessageChange({
                  ...message,
                  content: e.target.value
                });
              }}
              error={!message.content}
              helperText={!message.content ? "Message content is required" : ""}
            />
            <Box sx={{ mt: 1 }}>
              <VariableSelector
                variableCategories={variableCategories}
                onInsertVariable={handleInsertVariable}
                showVariables={showVariables}
                onToggleVariables={() => setShowVariables(!showVariables)}
              />
            </Box>
          </Grid>
        )}

        {channel === 'voice' && (
          <Grid item xs={12}>
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2, bgcolor: 'background.paper',  }}>
              <Typography variant="body2" color="text.secondary">
                Voice message content and configuration are managed through the voice platform settings below. 
                Each platform (Bland AI, VAPI, ElevenLabs, Twilio) has its own message configuration interface.
                Use the expand/collapse controls to manage the voice configuration sections.
              </Typography>
            </Box>
          </Grid>
        )}

        <Grid item xs={12}>
          {renderChannelConfig()}
        </Grid>
      </Grid>
    </Paper>
  );
};

ReminderMessageConfig.propTypes = {
  message: PropTypes.shape({
    template: PropTypes.string,
    content: PropTypes.string,
    email_config: PropTypes.object,
    sms_config: PropTypes.object,
    voice_config: PropTypes.object,
    chat_config: PropTypes.object,
  }).isRequired,
  onMessageChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  templates: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ).isRequired,
  communicationEndpoints: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })
  ).isRequired,
  channel: PropTypes.oneOf(['email', 'sms', 'voice', 'chat']).isRequired,
  index: PropTypes.number.isRequired,
  expandedVoiceConfigs: PropTypes.object,
  onToggleVoiceConfig: PropTypes.func,
  onExpandAllVoiceConfigs: PropTypes.func,
  onCollapseAllVoiceConfigs: PropTypes.func,
};

ReminderMessageConfig.defaultProps = {
  message: createDefaultMessage('email'),
  templates: [],
  communicationEndpoints: [],
  expandedVoiceConfigs: {},
  onToggleVoiceConfig: () => {},
  onExpandAllVoiceConfigs: () => {},
  onCollapseAllVoiceConfigs: () => {},
};

export default ReminderMessageConfig; 